import React, { useState, useEffect } from 'react';
import { ArrowUpDownIcon, RefreshCwIcon } from 'lucide-react';
import TokenSelector from './TokenSelector';
import SlippageSettings from './SlippageSettings';
import SwapButton from './SwapButton';
import SwapPreview from './SwapPreview';
import { TOKENS, DEFAULT_SLIPPAGE } from '../config/constants';
import { formatTokenAmount, debounce } from '../utils/helpers';
import { useSwapQuote } from '../hooks/useSwapQuote';
import { useTokenBalance } from '../hooks/useTokenBalance';

const SwapInterface = ({ 
  account, 
  signer, 
  onConnect, 
  onSwap, 
  isSwapping 
}) => {
  const [fromToken, setFromToken] = useState(TOKENS.FTM);
  const [toToken, setToToken] = useState(TOKENS.LIF3);
  const [fromAmount, setFromAmount] = useState('');
  const [slippage, setSlippage] = useState(DEFAULT_SLIPPAGE);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());

  // Custom hooks
  console.log('🔍 SwapInterface account state:', account);
  const { balance: fromBalance, loading: balanceLoading } = useTokenBalance(fromToken, account);
  console.log('💳 fromBalance in SwapInterface:', fromBalance, 'type:', typeof fromBalance);
  const { 
    quote: swapQuote, 
    loading: quoteLoading, 
    error: quoteError,
    fetchQuote 
  } = useSwapQuote();

  // Fetch quote when inputs change (with debouncing)
  useEffect(() => {
    console.log('🔄 SwapInterface useEffect triggered:', { 
      fromToken: fromToken?.symbol, 
      toToken: toToken?.symbol, 
      fromAmount 
    });
    
    const debouncedFetchQuote = debounce((token1, token2, amount) => {
      if (token1 && token2 && amount && parseFloat(amount) > 0) {
        console.log('⏰ Debounced fetch triggering...');
        fetchQuote(token1, token2, amount);
      }
    }, 1000);

    debouncedFetchQuote(fromToken, toToken, fromAmount);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromToken, toToken, fromAmount]); // Removed fetchQuote from deps to prevent infinite loop

  // Auto-refresh quotes every 15 seconds (reduced frequency to prevent flickering)
  useEffect(() => {
    const interval = setInterval(() => {
      if (fromToken && toToken && fromAmount && parseFloat(fromAmount) > 0) {
        fetchQuote(fromToken, toToken, fromAmount);
        setLastUpdateTime(Date.now());
      }
    }, 15000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromToken, toToken, fromAmount]); // Removed fetchQuote from deps to prevent infinite loop

  const handleSwapTokens = () => {
    const tempToken = fromToken;
    setFromToken(toToken);
    setToToken(tempToken);
    setFromAmount('');
  };

  const handleFromAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFromAmount(value);
    }
  };

  const handleMaxAmount = () => {
    if (fromBalance && parseFloat(fromBalance) > 0) {
      // Leave a small amount for gas if it's native FTM
      const maxAmount = fromToken.isNative ? 
        Math.max(0, parseFloat(fromBalance) - 0.01).toString() : 
        fromBalance;
      setFromAmount(maxAmount);
    }
  };

  const handleRefreshQuote = () => {
    if (fromToken && toToken && fromAmount && parseFloat(fromAmount) > 0) {
      fetchQuote(fromToken, toToken, fromAmount);
      setLastUpdateTime(Date.now());
    }
  };

  const canSwap = account && 
    fromToken && 
    toToken && 
    fromAmount && 
    parseFloat(fromAmount) > 0 && 
    fromToken.address !== toToken.address &&
    fromBalance &&
    parseFloat(fromAmount) <= parseFloat(fromBalance) &&
    swapQuote;

  const swapError = account && fromToken && toToken && fromAmount && parseFloat(fromAmount) > 0 ? 
    (parseFloat(fromAmount) > parseFloat(fromBalance || 0) ? 'Insufficient Balance' :
     quoteError ? 'Quote Error' : null) : null;

  const handleSwapClick = () => {
    if (canSwap && swapQuote) {
      onSwap({
        fromToken,
        toToken,
        fromAmount,
        estimatedOutput: swapQuote.amountOut,
        feeAmount: swapQuote.feeAmount,
        slippage
      });
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="swap-card rounded-2xl p-6 shadow-2xl">
        
        {/* Debug Panel - Remove this in production */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)', 
            padding: '1rem', 
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '12px',
            color: '#fff'
          }}>
            <div><strong>Debug Info:</strong></div>
            <div>• Quote Loading: {quoteLoading ? 'Yes' : 'No'}</div>
            <div>• Quote Error: {quoteError || 'None'}</div>
            <div>• From Amount: {fromAmount || 'None'}</div>
            <div>• Quote Available: {swapQuote ? 'Yes' : 'No'}</div>
            <div>• Account Connected: {account ? 'Yes' : 'No'}</div>
            <div>• Account Address: {account ? `${account.slice(0,6)}...${account.slice(-4)}` : 'None'}</div>
            <div>• Balance Loading: {balanceLoading ? 'Yes' : 'No'}</div>
            <div>• From Balance: {fromBalance || '0'}</div>
            <div style={{marginTop: '8px', fontSize: '11px'}}>
              📋 Check browser console (F12) for detailed logs
            </div>
            <button
              onClick={() => {
                console.log('🔧 Manual test triggered');
                const testProvider = new window.ethers.JsonRpcProvider('https://rpc.fantom.network');
                testProvider.getBlockNumber()
                  .then(blockNumber => console.log('✅ Manual network test successful, block:', blockNumber))
                  .catch(error => console.error('❌ Manual network test failed:', error));
              }}
              style={{
                marginTop: '8px',
                padding: '4px 8px',
                fontSize: '10px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '4px',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              Test Network
            </button>
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Swap Tokens</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefreshQuote}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors duration-200 border border-white/10 hover:border-white/20"
              title="Refresh Quote"
            >
              <RefreshCwIcon className={`w-4 h-4 text-white/80 ${quoteLoading ? 'animate-spin' : ''}`} />
            </button>
            <SlippageSettings slippage={slippage} onSlippageChange={setSlippage} />
          </div>
        </div>

        {/* From Token Section */}
        <div className="space-y-4 mb-4">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-white/60">From</span>
              <div className="text-sm text-white/60">
                Balance: {balanceLoading ? (
                  <span className="animate-pulse">Loading...</span>
                ) : (
                  <>
                    {fromBalance ? parseFloat(fromBalance).toFixed(4) : '0'} {fromToken?.symbol}
                    {fromBalance && parseFloat(fromBalance) > 0 && (
                      <button
                        onClick={handleMaxAmount}
                        className="ml-2 text-blue-400 hover:text-blue-300 text-xs"
                      >
                        MAX
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="0.0"
                  value={fromAmount}
                  onChange={handleFromAmountChange}
                  className="w-full bg-transparent text-2xl font-semibold text-white placeholder-white/30 outline-none"
                />
              </div>
              <div className="w-32">
                <TokenSelector
                  selectedToken={fromToken}
                  onTokenSelect={setFromToken}
                  excludeToken={toToken}
                  label=""
                />
              </div>
            </div>
          </div>

          {/* Swap Arrow */}
          <div className="flex justify-center">
            <button
              onClick={handleSwapTokens}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200 border border-white/20 hover:border-white/30 hover:rotate-180"
            >
              <ArrowUpDownIcon className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* To Token Section */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-white/60">To</span>
              <div className="text-sm text-white/60">
                {swapQuote && swapQuote.amountOut ? (
                  <>Est. {formatTokenAmount(swapQuote.amountOut, toToken?.decimals)} {toToken?.symbol}</>
                ) : (
                  <span>-</span>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <div className="text-2xl font-semibold text-white/60">
                  {quoteLoading ? (
                    <div className="animate-pulse">Loading...</div>
                  ) : swapQuote && swapQuote.amountOut ? (
                    formatTokenAmount(
                      (parseFloat(swapQuote.amountOut) - parseFloat(swapQuote.feeAmount || 0)).toString(),
                      toToken?.decimals
                    )
                  ) : (
                    '0.0'
                  )}
                </div>
              </div>
              <div className="w-32">
                <TokenSelector
                  selectedToken={toToken}
                  onTokenSelect={setToToken}
                  excludeToken={fromToken}
                  label=""
                />
              </div>
            </div>
          </div>
        </div>

        {/* Swap Preview */}
        <div className="mb-4">
          <SwapPreview
            fromToken={fromToken}
            toToken={toToken}
            fromAmount={fromAmount}
            estimatedOutput={swapQuote?.amountOut}
            feeAmount={swapQuote?.feeAmount}
            slippage={slippage}
            isLoading={quoteLoading}
          />
        </div>

        {/* Fee Disclaimer */}
        <div className="mb-4 p-3 bg-orange-500/10 border border-orange-400/20 rounded-lg">
          <p className="text-orange-200 text-sm text-center">
            ⚠️ A 1% fee will be deducted from your output tokens
          </p>
        </div>

        {/* Swap Button */}
        <SwapButton
          isConnected={!!account}
          onConnect={onConnect}
          canSwap={canSwap}
          onSwap={handleSwapClick}
          isSwapping={isSwapping}
          swapError={swapError}
          fromToken={fromToken}
          toToken={toToken}
          fromAmount={fromAmount}
          estimatedOutput={swapQuote?.amountOut}
        />

        {/* Last Updated */}
        {swapQuote && (
          <div className="mt-3 text-center text-xs text-white/40">
            Last updated: {new Date(lastUpdateTime).toLocaleTimeString()}
          </div>
        )}
      </div>
    </div>
  );
};

export default SwapInterface;